import { cva } from 'class-variance-authority';

/**
 * Split button group recipe. Figma: `Split button` (1993:130584) and `Split toggle` (1993:131183) on
 * the `Split button [In progress]` page — two sets in the file, one component here, because they
 * differ only in what sits in each half.
 *
 * THE GROUP OWNS THE SHAPE, the halves own everything else. Both halves are real `Button`s, so
 * colour, state, focus and motion come from the button recipe untouched. What the group adds is
 * geometry, and it adds it by setting two of the button's slots on its first and last child:
 *
 *   `--ds-btn-radius`         pill on the outer edge, 4px on the inner one
 *   `--ds-btn-content-shift`  the label or glyph nudged 2px toward the gap
 *
 * Slots rather than classes because a custom property INHERITS. The call bar's toggle half arrives
 * wrapped in one or two tooltip spans (`ToggleCamera`, `ToggleMicrophone`), so a child selector can
 * never reach the button inside it, while a variable set on the wrapper arrives there on its own.
 * That is what lets a caller put its own button in either half without this component knowing about
 * the wrapper.
 *
 * THE OUTER RADIUS IS NOT `--radius-full`, and cannot be. When the two radii on one side of a box add
 * up to more than that side, CSS scales EVERY corner of the box by one shared factor, so 9999px
 * beside 4px shrinks the 4px to nothing and the seam comes out square. It did exactly that in the
 * first Figma draft. `radius/lg` (24px) is already past half of every height from 32 to 44, so it
 * still draws a true pill; ExtraSmall (24px tall) takes `radius/md` (16px), because 24 + 4 would
 * exceed its height and trigger the same scaling.
 *
 * The inner 4px is a literal because no radius token is that small — `radius/sm` is 6px. It matches
 * the control-bar reference (Figma 1919:7301) and both hand-built pairs this replaces. Logged as a
 * design-system gap rather than rounded to 6.
 *
 * THE 2px NUDGE is optical. A half is a D: round on the outside, flat against the gap. The round side
 * holds less area, so the shape's visual centre sits about 2px toward the flat edge (2.4px measured on
 * the 44px square half), and a glyph centred by the box reads as pushed outward. Figma draws the fix
 * as asymmetric padding (14/10 on Large); a translate is the same result without re-stating every
 * size's padding, and it cannot move the box.
 *
 * `gap-0.5` is Figma's `x0,5`.
 */
export const splitButtonRecipe = cva(
  [
    'inline-flex items-stretch gap-0.5',
    '[&>:first-child]:[--ds-btn-radius:var(--ds-split-radius)_4px_4px_var(--ds-split-radius)]',
    '[&>:last-child]:[--ds-btn-radius:4px_var(--ds-split-radius)_var(--ds-split-radius)_4px]',
    '[&>:first-child]:[--ds-btn-content-shift:--spacing(0.5)]',
    '[&>:last-child]:[--ds-btn-content-shift:calc(--spacing(0.5)*-1)]',
  ],
  {
    variants: {
      size: {
        '2xs': '[--ds-split-radius:var(--radius-md)]',
        xs: '[--ds-split-radius:var(--radius-lg)]',
        regular: '[--ds-split-radius:var(--radius-lg)]',
        large: '[--ds-split-radius:var(--radius-lg)]',
      },
      fullWidth: {
        true: 'flex w-full',
        false: '',
      },
    },
    defaultVariants: { size: 'regular', fullWidth: false },
  }
);

/**
 * The menu half, while its menu is open.
 *
 * Figma's `State=Menu open` draws the chevron in its PRESSED cell, so the half reads as held down for
 * as long as the menu it owns is on screen. `aria-expanded` is the state that means that: Base UI's
 * `Menu.Trigger` sets it, and a caller driving a legacy menu sets it itself.
 *
 * The button recipe's hover rule still outranks this, so pointing at an open trigger shows hover.
 * That is the right way round — the pointer is usually in the menu by then.
 */
export const splitButtonTriggerRecipe = cva([
  '[&[aria-expanded=true]:not([data-disabled])]:bg-[var(--ds-btn-bg-pressed)]',
  '[&[aria-expanded=true]:not([data-disabled])]:text-[var(--ds-btn-fg-pressed)]',
  '[&[aria-expanded=true]:not([data-disabled])]:border-[var(--ds-btn-border-pressed)]',
]);
